import { SupplierBusyPeriodDto } from "api/busy-period.api";
import { BusyPeriodFormValues, SupplierBusyPeriod } from "pages/out-of-office/out-of-office.typing";

/**
 * Busy periods are calendar dates (no time-of-day).
 * Encode the selected local calendar day as UTC midnight so timezone conversion
 * cannot shift the day (unlike Date#toISOString on local midnight).
 */
export const toApiDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00:00.000Z`;
};

/** Decode API datetime to a Date at local midnight of the calendar day in the payload. */
export const fromApiDate = (value: string | Date): Date => {
  if (value instanceof Date) {
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
  }

  // Instant with offset/Z: calendar day is the UTC date (matches toApiDate encoding)
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(value.trim())) {
    const parsed = new Date(value);
    return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  }

  // Date-only or naive datetime: trust the written Y-M-D
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const parsed = new Date(value);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

export const mapBusyPeriodDto = (dto: SupplierBusyPeriodDto): SupplierBusyPeriod => ({
  id: String(dto.id),
  startDate: fromApiDate(dto.startDate),
  endDate: fromApiDate(dto.endDate),
  canAcceptSmallAssignments: dto.canAcceptSmallAssignments,
  comments: dto.comments?.trim() ? dto.comments : undefined,
});

export const toBusyPeriodPayload = (values: BusyPeriodFormValues) => ({
  startDate: toApiDate(values.startDate!),
  endDate: toApiDate(values.endDate!),
  canAcceptSmallAssignments: values.canAcceptSmallAssignments,
  comments: values.comments || undefined,
});
